import { PopoverController, PopoverPosition } from "@amzn/stencil-react-components/dist/submodules/popover";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { SelectProps } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Label } from "@amzn/stencil-react-components/text";
import { IconSize } from "@amzn/stencil-react-icons";
import { Checkbox as UXMCheckbox } from "@amzn/uxm-stencil-react-components/lib/src/stencil/checkbox";
import { isArray } from "lodash";
import React, { FC, ReactNode, useRef, useState } from "react";

import categoriesOverride from "../../../configuration/AIForMe/categoriesOverride.json";
import InfoIconButton from "../InfoIconButton";

export type MultiSelectProps = Omit<SelectProps<string>, "onChange"> & {
  onChange: (value: string[]) => void;
  showToolTip?: (ReactNode | undefined)[];
  columns: number;
  touchpoint: string;
  isLoading?: boolean;
};

export const MultiSelect: FC<MultiSelectProps> = (props: MultiSelectProps) => {
  let values: string[] = [];
  if (props.value) {
    if (!isArray(props.value)) {
      values = [props.value];
    } else {
      values = props.value;
    }
  }

  const showToolTip = props.showToolTip || props.options.map(() => undefined);

  // State for managing popover open/close for each option
  const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});
  const popoverRefs = useRef<Record<string, React.RefObject<HTMLButtonElement>>>({});

  // Initialize refs for each option
  props.options.forEach((option) => {
    if (!popoverRefs.current[option]) {
      popoverRefs.current[option] = React.createRef<HTMLButtonElement>();
    }
  });

  const togglePopover = (option: string) => {
    setOpenPopovers((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  // Hard to test from a single component on clicking on onther components to close it
  /* istanbul ignore next */
  const closePopover = (option: string) => {
    setOpenPopovers((prev) => ({ ...prev, [option]: false }));
  };

  if (props.isLoading) {
    return <Spinner size={SpinnerSize.Small}></Spinner>;
  }

  const options = props.options.map((option: string, index: number) => {
    const checked = values.includes(option);
    const target = popoverRefs.current[option];
    const isOpen = openPopovers[option] || false;

    // This is a temporary measure to change the display name of a category
    // This will require a refactor on Catalogical side and this was required as urgent
    const getOptionDisplayName = (option: string) =>
      (categoriesOverride as { [key: string]: string })[option] ?? option;

    return (
      <Row alignItems="center" gridGap="S200" flex={1} key={option}>
        <UXMCheckbox
          id={option}
          value={option}
          checked={checked}
          data-testid="multiselect-option"
          onChange={(e: { target: { checked: any; value: string } }) => {
            if (props.onChange) {
              if (e.target.checked) {
                props.onChange([...values, e.target.value]);
              } else {
                props.onChange(values.filter((v: string) => v !== e.target.value));
              }
            }
          }}
          touchpoint={props.touchpoint}
        />
        <Label htmlFor={option}>{getOptionDisplayName(option)}</Label>
        {!!showToolTip[index] && (
          <>
            <InfoIconButton
              ref={target}
              iconSize={IconSize.ExtraSmall}
              data-testid="shared-list-popover"
              onClick={(e) => {
                togglePopover(option);
                e.preventDefault();
                e.stopPropagation();
              }}
            />
            <PopoverController
              target={target}
              isOpen={isOpen}
              close={() => closePopover(option)}
              position={PopoverPosition.TopCenter}
              data-testid={`popover-${option}`}
              data-is-open={isOpen}
            >
              <div style={{ padding: "8px 12px", maxWidth: "300px" }}>{showToolTip[index]}</div>
            </PopoverController>
          </>
        )}
      </Row>
    );
  });

  function renderRows(options: React.JSX.Element[], columns: number): React.ReactNode[] {
    const rows: React.ReactNode[] = [];
    for (let i = 0; i < options.length; i += columns) {
      const rowOptions = options.slice(i, i + columns);
      if (rowOptions.length < columns) {
        for (let j = rowOptions.length; j < columns; j++) {
          rowOptions.push(<Col flex={1} key={`empty-${i}-${j}`} />);
        }
      }
      rows.push(
        <Row gridGap="S200" key={i}>
          {rowOptions}
        </Row>
      );
    }
    return rows;
  }

  return (
    <Col rowGap="5px" {...props} onChange={() => {}}>
      {renderRows(options, props.columns)}
    </Col>
  );
};
